/**
 * TradeStack - Job Form Page (Create/Edit)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams, Link } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import api from '../api';
import '../styles/Forms.css';

function JobForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { config } = useConfig();

  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [loadingJob, setLoadingJob] = useState(isEditMode);
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);

  const customerId = searchParams.get('customerId');

  const [formData, setFormData] = useState({
    customerId: customerId || '',
    title: '',
    description: '',
    addressLine1: '',
    status: 'lead',
    scheduledStart: '',
    estimatedHours: '',
    estimatedValue: ''
  });

  useEffect(() => {
    loadCustomers();
    if (isEditMode) {
      loadJob();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadCustomers = async () => {
    try {
      const response = await api.getCustomers({ limit: 1000 });
      setCustomers(response.data.customers || []);
    } catch (err) {
      console.error('Failed to load customers:', err);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const loadJob = async () => {
    try {
      const response = await api.getJob(id);
      const job = response.data.job;
      setFormData({
        customerId: job.customer_id || '',
        title: job.title || '',
        description: job.description || '',
        addressLine1: job.address_line1 || '',
        status: job.status || 'lead',
        scheduledStart: job.scheduled_start ? job.scheduled_start.split('T')[0] : '',
        estimatedHours: job.estimated_hours || '',
        estimatedValue: job.estimated_value || ''
      });
    } catch (err) {
      setError('Failed to load job');
      console.error(err);
    } finally {
      setLoadingJob(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = {
        customerId: formData.customerId || null,
        title: formData.title,
        description: formData.description,
        addressLine1: formData.addressLine1,
        status: formData.status,
        scheduledStart: formData.scheduledStart || null,
        estimatedHours: formData.estimatedHours ? parseFloat(formData.estimatedHours) : null,
        estimatedValue: formData.estimatedValue ? parseFloat(formData.estimatedValue) : null
      };

      if (isEditMode) {
        await api.updateJob(id, submitData);
        navigate(`/jobs/${id}`);
      } else {
        const response = await api.createJob(submitData);
        navigate(`/jobs/${response.data.job.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} job`);
    } finally {
      setLoading(false);
    }
  };

  // Config uses 'name', fallback uses 'name' for consistency
  const statuses = config?.jobStatuses || [
    { id: 'lead', name: 'Lead' },
    { id: 'quoted', name: 'Quoted' },
    { id: 'scheduled', name: 'Scheduled' },
    { id: 'in_progress', name: 'In Progress' },
    { id: 'completed', name: 'Completed' }
  ];

  // Debug: log statuses to verify data
  console.log('JobForm statuses:', statuses);


  if (loadingJob) {
    return <div className="form-page"><div className="loading-state">Loading...</div></div>;
  }

  return (
    <div className="form-page">
      <div className="detail-header">
        <div className="header-left">
          <Link to={isEditMode ? `/jobs/${id}` : '/jobs'} className="back-link">
            &larr; {isEditMode ? 'Back to Job' : 'Back to Jobs'}
          </Link>
          <h1>{isEditMode ? 'Edit Job' : 'New Job'}</h1>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit} className="form-card">
        <div className="form-section">
          <h3>Job Details</h3>

          <div className="form-group">
            <label htmlFor="customerId">Customer</label>
            <select
              id="customerId"
              name="customerId"
              value={formData.customerId}
              onChange={handleChange}
              disabled={loadingCustomers}
              style={{ color: '#1f2937' }}
            >
              <option value="" style={{ color: '#1f2937', backgroundColor: '#ffffff' }}>-- No Customer --</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id} style={{ color: '#1f2937', backgroundColor: '#ffffff' }}>
                  {customer.company_name || `${customer.first_name} ${customer.last_name}`}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="title">Job Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="e.g., Kitchen Remodel, Deck Build"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="Describe the scope of work..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="addressLine1">Job Address</label>
            <input
              type="text"
              id="addressLine1"
              name="addressLine1"
              value={formData.addressLine1}
              onChange={handleChange}
              placeholder="123 Main St, City, State ZIP"
            />
          </div>

          <div className="form-group">
            <label>Status</label>
            <div className="status-button-group">
              {statuses.map(status => {
                const isSelected = formData.status === status.id;
                return (
                  <button
                    key={status.id}
                    type="button"
                    className={`status-btn ${isSelected ? 'active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, status: status.id }))}
                    style={{
                      color: isSelected ? '#ffffff' : '#1f2937',
                      backgroundColor: isSelected ? '#2563eb' : '#f3f4f6',
                      padding: '8px 16px',
                      border: isSelected ? '2px solid #2563eb' : '2px solid #e5e7eb',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    {status.name || status.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Schedule & Estimate</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="scheduledStart">Scheduled Date</label>
              <input
                type="date"
                id="scheduledStart"
                name="scheduledStart"
                value={formData.scheduledStart}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="estimatedHours">Estimated Hours</label>
              <input
                type="number"
                id="estimatedHours"
                name="estimatedHours"
                value={formData.estimatedHours}
                onChange={handleChange}
                min="0"
                step="0.5"
                placeholder="0"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="estimatedValue">Estimated Value ($)</label>
            <input
              type="number"
              id="estimatedValue"
              name="estimatedValue"
              value={formData.estimatedValue}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="0.00"
            />
          </div>
        </div>

        <div className="form-actions">
          <Link to={isEditMode ? `/jobs/${id}` : '/jobs'} className="btn btn-secondary">Cancel</Link>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create Job')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default JobForm;
